import { useEffect, useRef, useState, Suspense } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowDown } from "lucide-react";
import { Scene } from "./three/Scene";
import { stageState, CHAPTERS, CHAPTER_COUNT } from "../lib/stageState";
import { Button } from "./ui/button";
import { useWaitlist } from "./WaitlistDialog";

gsap.registerPlugin(ScrollTrigger);

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
};

const StageCanvas = () => (
  <Canvas
    className="axt-canvas"
    style={{ position: "absolute", inset: 0 }}
    dpr={[1, 1.5]}
    gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
    camera={{ fov: 38, near: 0.1, far: 60, position: [0, 0.2, 9.5] }}
    onCreated={({ gl }) => {
      gl.toneMapping = THREE.ACESFilmicToneMapping;
      gl.toneMappingExposure = 1.15;
      gl.setClearColor(0x000000, 0);
    }}
  >
    <Suspense fallback={null}>
      <Scene />
    </Suspense>
  </Canvas>
);

const ChapterCopy = ({ chapter, isHero, refCb }) => {
  const { open } = useWaitlist();
  return (
  <div
    ref={refCb}
    data-testid={`chapter-${chapter.id}`}
    className="absolute inset-x-0 bottom-0 px-5 sm:px-8 lg:px-12 pb-16 sm:pb-20 lg:pb-24 pointer-events-none"
    style={{ opacity: isHero ? 1 : 0, visibility: isHero ? "visible" : "hidden" }}
  >
    <div className="max-w-6xl mx-auto">
      <div className="max-w-[46rem] pointer-events-auto">
        <p className="axt-rule mb-5" data-testid={`chapter-${chapter.id}-eyebrow`}>
          <span>{chapter.eyebrow}</span>
        </p>
        <h1
          data-testid={`chapter-${chapter.id}-headline`}
          className={`font-display tracking-[-0.02em] text-[color:var(--axt-text)] ${
            isHero
              ? "text-4xl sm:text-5xl lg:text-6xl xl:text-[4.6rem] leading-[1.02]"
              : "text-3xl sm:text-4xl lg:text-5xl leading-[1.05]"
          }`}
        >
          {chapter.title}
        </h1>
        <p
          data-testid={`chapter-${chapter.id}-subhead`}
          className="mt-5 sm:mt-6 text-sm sm:text-base lg:text-lg leading-relaxed text-[color:var(--axt-text-dim)] max-w-[36rem]"
        >
          {chapter.body}
        </p>
        {isHero && (
          <div className="mt-8" data-testid="hero-actions">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                data-testid="hero-see-story-button"
                onClick={() => open("hero-see-it-run")}
                className="axt-btn-primary h-12 px-6 focus-visible:ring-1 focus-visible:ring-white/70"
              >
                See it run
              </Button>
              <Button
                data-testid="hero-book-walkthrough-button"
                variant="ghost"
                onClick={() => open("hero-talk")}
                className="axt-btn-ghost h-12 px-6"
              >
                Talk to a fund accountant
              </Button>
            </div>
            <p className="mt-5 font-mono-axt text-[11px] tracking-[0.14em] uppercase text-[color:var(--axt-text-faint)]" data-testid="hero-microline">
              1,200 LP capital notices. 30 minutes. Every one reviewed before it sends.
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

const ChapterRail = ({ active }) => (
  <div
    data-testid="chapter-rail"
    className="hidden md:flex absolute right-8 lg:right-12 top-1/2 -translate-y-1/2 flex-col gap-5 pointer-events-none z-10"
    aria-hidden="true"
  >
    {CHAPTERS.map((c, i) => {
      const on = i === active;
      return (
        <div key={c.id} className="flex items-center justify-end gap-3">
          <span
            className="font-mono-axt text-[10px] tracking-[0.2em] uppercase"
            style={{
              color: on ? "var(--axt-accent)" : "var(--axt-text-faint)",
              opacity: on ? 1 : 0.6,
              transition: "color 300ms ease, opacity 300ms ease",
            }}
          >
            {c.rail}
          </span>
          <span
            className="block"
            style={{
              width: on ? 22 : 6,
              height: 6,
              background: on ? "var(--axt-accent)" : "rgba(255,255,255,0.22)",
              boxShadow: on ? "0 0 12px var(--axt-glow-strong)" : "none",
              transition: "width 400ms cubic-bezier(0.16,1,0.3,1), background-color 300ms ease",
            }}
          />
        </div>
      );
    })}
  </div>
);

export const Stage3D = () => {
  const reduced = usePrefersReducedMotion();
  const storyRef = useRef(null);
  const stickyRef = useRef(null);
  const overlays = useRef([]);
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);

  useEffect(() => {
    stageState.p = 0;
    stageState.reduced = reduced;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: storyRef.current,
          start: "top top",
          end: "bottom bottom",
          // reduced motion: no smoothing lag, the scene follows the finger exactly
          scrub: reduced ? true : 1.25,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const idx = Math.min(CHAPTER_COUNT - 1, Math.floor(self.progress * CHAPTER_COUNT));
            if (idx !== activeRef.current) {
              activeRef.current = idx;
              setActive(idx);
            }
          },
        },
      });

      // master scalar: 0 → 5
      tl.to(stageState, { p: CHAPTER_COUNT, duration: CHAPTER_COUNT }, 0);

      overlays.current.forEach((el, i) => {
        if (!el) return;
        if (i === 0) {
          tl.set(el, { autoAlpha: 1, y: 0 }, 0);
          tl.to(el, { autoAlpha: 0, y: -50, duration: 0.32, ease: "power2.in" }, 0.55);
        } else {
          tl.fromTo(el, { autoAlpha: 0, y: 60 }, { autoAlpha: 1, y: 0, duration: 0.38, ease: "power3.out" }, i + 0.12);
          if (i < CHAPTER_COUNT - 1) {
            tl.to(el, { autoAlpha: 0, y: -50, duration: 0.3, ease: "power2.in" }, i + 0.66);
          }
        }
      });

      // hero intro — slow, weighted
      const hero = overlays.current[0];
      if (hero && !reduced) {
        gsap.from(hero.querySelectorAll("p, h1, [data-testid='hero-actions']"), {
          y: 46,
          opacity: 0,
          duration: 1.5,
          stagger: 0.14,
          ease: "expo.out",
          delay: 0.25,
        });
      }
    }, storyRef);

    // pointer parallax (desktop only)
    const onMove = (e) => {
      stageState.pointerX = (e.clientX / window.innerWidth - 0.5) * 2;
      stageState.pointerY = -(e.clientY / window.innerHeight - 0.5) * 2;
    };
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    const parallax = !isTouch && !reduced;
    if (parallax) window.addEventListener("pointermove", onMove, { passive: true });

    // ScrollTrigger measures on mount; refresh once layout/fonts settle and on orientation change
    const refresh = () => ScrollTrigger.refresh();
    const t = setTimeout(refresh, 800);
    window.addEventListener("orientationchange", refresh);

    return () => {
      clearTimeout(t);
      window.removeEventListener("orientationchange", refresh);
      ctx.revert();
      if (parallax) window.removeEventListener("pointermove", onMove);
    };
  }, [reduced]);

  return (
    <section
      id="story"
      ref={storyRef}
      data-testid="story-section"
      className="relative"
      style={{ height: `${CHAPTER_COUNT * 140}vh` }}
    >
      <div ref={stickyRef} className="sticky top-0 h-[100svh] overflow-hidden axt-stage-bg">
        <StageCanvas />
        <div className="absolute inset-0 axt-noise pointer-events-none" />
        <div className="absolute inset-0 axt-vignette pointer-events-none" />

        {CHAPTERS.map((c, i) => (
          <ChapterCopy key={c.id} chapter={c} isHero={i === 0} refCb={(el) => (overlays.current[i] = el)} />
        ))}

        <ChapterRail active={active} />

        <div
          data-testid="scroll-cue"
          className="absolute right-5 sm:right-8 bottom-6 flex items-center gap-3 text-[color:var(--axt-text-faint)] pointer-events-none md:hidden"
          style={{ opacity: active === 0 ? 1 : 0, transition: "opacity 400ms ease" }}
        >
          <span className="font-mono-axt text-[10px] tracking-[0.2em] uppercase">Scroll</span>
          <ArrowDown className="w-4 h-4 axt-scroll-cue" />
        </div>
        <div
          className="hidden md:flex absolute left-1/2 -translate-x-1/2 bottom-6 flex-col items-center gap-2 text-[color:var(--axt-text-faint)] pointer-events-none"
          style={{ opacity: active === 0 ? 1 : 0, transition: "opacity 400ms ease" }}
          aria-hidden="true"
        >
          <span className="font-mono-axt text-[10px] tracking-[0.24em] uppercase">Scroll to begin</span>
          <ArrowDown className="w-4 h-4 axt-scroll-cue" />
        </div>
      </div>
    </section>
  );
};
